import {keyBy} from 'lodash';

import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {CancelError} from '../../../helpers/asyncCancelHelpers';
import {ActionWithPayload} from '../../../helpers/reduxHelpers';
import {CompanyModel} from '../../../models/api/companyModel';
import {ApiIntegrationErrors, IntegrationErrorModel} from '../../../models/api/integrationErrorModel';
import {fetchApiAsync} from '../../../services/http/http';
import {HttpNetworkError, HttpTimeoutError} from '../../../services/http/httpError';
import {ThunkAction} from '../../appThunk';
import {ResourceMap} from '../../appTypes';
import {getCompany} from '../company/companySelectors';
import {getIntegrationErrors} from '../dataSelectors';

/*
 * Action types.
 */

export enum IntegrationErrorsActionsEnum {
  INTEGRATION_ERRORS_INITIALIZED = '@@data/integration_errors/INTEGRATION_ERROR_INITIALIZED',
  INTEGRATION_ERROR_UPDATED = '@@data/integration_errors/INTEGRATION_ERROR_UPDATED',
  INTEGRATION_ERROR_DELETED = '@@data/integration_errors/INTEGRATION_ERROR_DELETED',
}

/*
 * Types.
 */

export interface IntegrationErrorInitializedAction
  extends ActionWithPayload<ResourceMap<IntegrationErrorModel>> {
  type: IntegrationErrorsActionsEnum.INTEGRATION_ERRORS_INITIALIZED;
}

export interface IntegrationErrorUpdatedAction extends ActionWithPayload<IntegrationErrorModel> {
  type: IntegrationErrorsActionsEnum.INTEGRATION_ERROR_UPDATED;
}

export interface IntegrationErrorDeletedAction extends ActionWithPayload<number> {
  type: IntegrationErrorsActionsEnum.INTEGRATION_ERROR_DELETED;
}

/*
 * Action creators.
 */

export function initializeIntegrationErrorsAction(): ThunkAction {
  return async (dispatch, getState) => {
    try {
      const company = getCompany(getState());
      const allErrors = await fetchInitialIntegrationErrorsAsync(company);

      const action: IntegrationErrorInitializedAction = {
        type: IntegrationErrorsActionsEnum.INTEGRATION_ERRORS_INITIALIZED,
        payload: keyBy(allErrors, (t) => t.id),
      };

      dispatch(action);
    } catch (error) {
      if (
        error instanceof CancelError ||
        error instanceof HttpTimeoutError ||
        error instanceof HttpNetworkError
      ) {
        return;
      }

      reportToBugsnag(new ExtendedError('Error while fetching integration errors.', error));
    }
  };
}

function fetchInitialIntegrationErrorsAsync(company: CompanyModel) {
  const url = `${company.url}/integration_errors`;
  return fetchApiAsync(ApiIntegrationErrors, url);
}

export function updateIntegrationErrorAction(
  newIntegrationError: IntegrationErrorModel,
): IntegrationErrorUpdatedAction {
  return {
    type: IntegrationErrorsActionsEnum.INTEGRATION_ERROR_UPDATED,
    payload: newIntegrationError,
  };
}

export function deleteIntegrationErrorAction(integrationErrorId: number): ThunkAction {
  return (dispatch, getState) => {
    const existingIntegrationError = getIntegrationErrors(getState())[integrationErrorId];
    if (!existingIntegrationError) {
      return undefined;
    }

    const action: IntegrationErrorDeletedAction = {
      type: IntegrationErrorsActionsEnum.INTEGRATION_ERROR_DELETED,
      payload: integrationErrorId,
    };

    return dispatch(action);
  };
}

/*
 * Union type.
 */

export type IntegrationErrorsAction =
  | IntegrationErrorInitializedAction
  | IntegrationErrorUpdatedAction
  | IntegrationErrorDeletedAction;
